// The accounting system the ledger export goes to (technical design §8.2): which QuickBooks Online
// or Xero company is connected, connecting one (the provider's consent page, then back here) and
// disconnecting. Coming back from the provider, the hash says how it went.

import { useEffect, useState } from 'react';
import { refusalOf, RpcError } from '@flowology/client';
import { client } from '../api.ts';
import type { BooksProps } from './props.ts';
import { dayText } from './model.ts';
import { LedgerExport } from './LedgerExport.tsx';
import { Loading, Panel, useAct, useQuery } from './ui.tsx';
import { useDesign } from '../blueprint/design.ts';

type Target = 'quickbooks' | 'xero';
const LABEL: Record<Target, string> = { quickbooks: 'QuickBooks Online', xero: 'Xero' };

interface Connection {
  target: Target;
  fake: boolean;
  companyName: string | null;
  connectedBy: string;
  connectedAt: string;
}

/** Back from the provider: #ledger=<target>_<connected|declined|failed>, read once as the page loads. */
const RETURNED = /^#ledger=(quickbooks|xero)_(connected|declined|failed|one_org)$/.exec(location.hash);
let untold = RETURNED !== null;
/** This page load came back from the provider (the office opens on the books). */
export const CAME_BACK_FROM_LEDGER = RETURNED !== null;
/** Until the panel has said how it went: the books open on it. */
export const ledgerReturnUntold = () => untold;

const RETURN_TEXT = {
  connected: (t: Target) => `${LABEL[t]} is connected.`,
  declined: (t: Target) => `${LABEL[t]} was not connected: the approval was cancelled.`,
  failed: (t: Target) => `${LABEL[t]} did not accept the connection. Try again.`,
  one_org: (t: Target) =>
    `${LABEL[t]} wasn’t connected: choose exactly one organisation on its page, then connect again.`,
};

/** The connect route is plain HTTP (it answers with the provider's link), with the CSRF header. */
export async function connectUrl(target: Target): Promise<string> {
  const csrf = (await client.session())?.csrf;
  const res = await fetch(`/integrations/${target}/connect`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json', ...(csrf ? { 'x-flow-csrf': csrf } : {}) },
    body: '{}',
  });
  const body = (await res.json().catch(() => null)) as {
    ok?: boolean;
    result?: { url: string };
    error?: unknown;
  } | null;
  // A refusal says what to do, ours or the way there's (a 413, a proxy's 502), as the client does.
  if (!res.ok || !body?.ok || !body.result) throw new RpcError(res.status, refusalOf(res.status, body));
  return body.result.url;
}

export function Connections({ toast, timeZone }: Pick<BooksProps, 'toast' | 'timeZone'>) {
  const q = useQuery<{ connection: Connection | null }>('exports.ledgerConnection', {});
  const act = useAct(toast, () => q.reload());
  const [busy, setBusy] = useState(false);
  const bp = useDesign() === 'blueprint';
  useEffect(() => {
    if (!untold || !RETURNED) return;
    untold = false;
    toast(RETURN_TEXT[RETURNED[2] as keyof typeof RETURN_TEXT](RETURNED[1] as Target));
    history.replaceState(null, '', location.pathname + location.search);
  }, []);
  const connect = (t: Target) => {
    setBusy(true);
    void connectUrl(t)
      .then((url) => location.assign(url))
      .catch((e: unknown) => {
        toast(e instanceof Error ? e.message : 'That did not work. Try again.');
        setBusy(false);
      });
  };
  if (!q.data) return <Loading error={q.error} what="the accounting system" retry={() => void q.reload()} />;
  const c = q.data.connection;
  return (
    <>
      <Panel
        title="Accounting system"
        aside={<span className={`chip say ${c ? 'ok' : ''}`}>{c ? 'Connected' : 'Not connected'}</span>}
      >
        {c ? (
          <p>
            The ledger export goes to <strong>{LABEL[c.target]}</strong>
            {c.companyName ? <> ({c.companyName})</> : null}, connected {dayText(c.connectedAt)}.
            {c.fake ? (
              <span className="muted small"> A stand-in for testing: nothing leaves Flowology.</span>
            ) : null}
          </p>
        ) : (
          <p className="muted">
            Connect the company’s QuickBooks Online or Xero to export the books there. You approve it on their
            site and come back here.
          </p>
        )}
        <div className="row-actions">
          {(['quickbooks', 'xero'] as const).map((t) => (
            <button
              key={t}
              // Connecting is the panel's one primary only while nothing is connected; in Blueprint, one
              // primary in a zone (DESIGN.md §3a): QuickBooks's, Xero's beside it quiet
              className={`btn ${c || (bp && t === 'xero') ? 'quiet' : 'primary'}`}
              disabled={busy || act.busy}
              onClick={() => connect(t)}
            >
              {c?.target === t ? `Connect ${LABEL[t]} again` : `Connect ${LABEL[t]}`}
            </button>
          ))}
          {c && (
            <button
              className="btn quiet"
              disabled={busy || act.busy}
              onClick={() => {
                if (confirm(`Disconnect ${LABEL[c.target]}? What was exported stays there.`))
                  void act.run('exports.disconnectLedger', {}, `${LABEL[c.target]} is disconnected.`);
              }}
            >
              Disconnect
            </button>
          )}
        </div>
      </Panel>
      {c && (
        <LedgerExport key={c.target} target={c.target} toast={toast} {...(timeZone ? { timeZone } : {})} />
      )}
    </>
  );
}
